import { supabase } from '@/lib/supabaseClient';
import { ThemeConfig, DEFAULT_THEME, PRESET_THEMES } from '@/types/theme';
import { fixThemeForegrounds } from '@/utils/themeFixer';
import { getContrastingTextColor, generateHoverColor } from '@/utils/themeUtils';
import { applySEOSettings } from '@/utils/seo';

// Function to get current tenant ID dynamically
const getCurrentTenantId = async (): Promise<string | null> => {
  try {
    const { data, error } = await supabase.rpc('get_current_user_tenant_id');
    if (error) {
      return null;
    }
    return data;
  } catch (error) {
    return null;
  }
};

export interface TenantThemeSettings {
  current_theme: string;
  custom_logo?: string;
  custom_favicon?: string;
  custom_themes?: ThemeConfig[];
  sidebar_color_override?: string;
  custom_seo?: any;
}

// Function to load tenant theme settings from database
export const loadTenantThemeSettings = async (): Promise<TenantThemeSettings | null> => {
  try {
    const tenantId = await getCurrentTenantId();
    if (!tenantId) {
      return null;
    }

    const { data, error } = await supabase
      .from('tenant_theme_settings')
      .select('*')
      .eq('tenant_id', tenantId)
      .single();

    if (error) {
      return null;
    }

    return data;
  } catch (error) {
    return null;
  }
};

// Function to load tenant theme settings by tenant ID (for customer portal)
export const loadTenantThemeSettingsByTenantId = async (tenantId: string): Promise<TenantThemeSettings | null> => {
  try {
    if (!tenantId) {
      return null;
    }

    const { data, error } = await supabase
      .from('tenant_theme_settings')
      .select('*')
      .eq('tenant_id', tenantId)
      .single();

    if (error) {
      return null;
    }

    return data;
  } catch (error) {
    return null;
  }
};

// Function to apply tenant theme to the page
export const applyTenantTheme = (settings: TenantThemeSettings | null, tenantName?: string) => {
  if (!settings) {
    // Apply default theme if no settings found
    applyThemeToPage(DEFAULT_THEME, tenantName);
    return;
  }

  // Find the current theme (preset or custom)
  let theme: ThemeConfig = DEFAULT_THEME;
  
  if (settings.current_theme) {
    // First check preset themes
    const presetTheme = PRESET_THEMES.find(t => t.id === settings.current_theme);
    if (presetTheme) {
      theme = presetTheme;
    } else if (settings.custom_themes) {
      // Then check custom themes
      const customTheme = settings.custom_themes.find(t => t.id === settings.current_theme);
      if (customTheme) {
        theme = customTheme;
      }
    }
  }

  // Apply custom logo and favicon if they exist
  if (settings.custom_logo) {
    theme = {
      ...theme,
      logo: settings.custom_logo,
    };
  }
  
  if (settings.custom_favicon) {
    theme = {
      ...theme,
      favicon: settings.custom_favicon,
    };
  }

  // Apply sidebar color override if it exists and the theme is not custom
  if (settings.sidebar_color_override && !theme.isCustom) {
    theme = {
      ...theme,
      colors: {
        ...theme.colors,
        sidebarColor: settings.sidebar_color_override,
        sidebarTextColor: getContrastingTextColor(settings.sidebar_color_override),
        sidebarHoverBgColor: generateHoverColor(settings.sidebar_color_override, 0.15),
        sidebarHoverTextColor: getContrastingTextColor(generateHoverColor(settings.sidebar_color_override, 0.15)),
      },
    };
  }

  // Apply the theme to the page
  applyThemeToPage(theme, tenantName);

  // Custom SEO is now handled by ThemeContext to prevent flickering
};

// Function to apply theme to the page
const applyThemeToPage = (theme: ThemeConfig, tenantName?: string) => {
  // Fix theme foregrounds before applying
  const fixedTheme = fixThemeForegrounds(theme);
  const root = document.documentElement;
  
  // Apply color variables
  Object.entries(fixedTheme.colors).forEach(([key, value]) => {
    root.style.setProperty(`--${key}`, value);
  });

  // Apply logo and favicon
  if (fixedTheme.logo) {
    root.style.setProperty('--logo-url', `url(${fixedTheme.logo})`);
  }
  if (fixedTheme.favicon) {
    root.style.setProperty('--favicon-url', `url(${fixedTheme.favicon})`);
  }

  // Apply sidebar colors
  if (fixedTheme.colors.sidebarColor) {
    root.style.setProperty('--sidebar-color', fixedTheme.colors.sidebarColor);
  }
  if (fixedTheme.colors.sidebarTextColor) {
    root.style.setProperty('--sidebar-text-color', fixedTheme.colors.sidebarTextColor);
  }
  if (fixedTheme.colors.sidebarHoverBgColor) {
    root.style.setProperty('--sidebar-hover-bg-color', fixedTheme.colors.sidebarHoverBgColor);
  }
  if (fixedTheme.colors.sidebarHoverTextColor) {
    root.style.setProperty('--sidebar-hover-text-color', fixedTheme.colors.sidebarHoverTextColor);
  }

  // Update favicon in head
  const faviconLink = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
  if (faviconLink && fixedTheme.favicon) {
    faviconLink.href = fixedTheme.favicon;
  }

  // SEO is now handled by ThemeContext to prevent flickering
};

// Function to get the current logo URL from theme settings
export const getTenantLogo = async (): Promise<string | null> => {
  const settings = await loadTenantThemeSettings();
  if (settings?.custom_logo) {
    return settings.custom_logo;
  }
  return null;
};

// Function to get the current favicon URL from theme settings
export const getTenantFavicon = async (): Promise<string | null> => {
  const settings = await loadTenantThemeSettings();
  if (settings?.custom_favicon) {
    return settings.custom_favicon;
  }
  return null;
}; 